import { useEffect, useState } from "react";
import { ethers } from "ethers";
import { provider, ERC20_ABI, fmtUnits, shortHex, ago, type Manifest } from "../chain";
import { BRAND } from "../Brand";

interface Head { number: number; timestamp: number; txs: number }

export default function Overview({ m }: { m: Manifest }) {
  const [head, setHead] = useState<Head>();
  const [supply, setSupply] = useState<bigint>();
  const [err, setErr] = useState<string>();

  useEffect(() => {
    let live = true;
    const tick = async () => {
      try {
        const p = provider();
        const b = await p.getBlock("latest");
        const token = new ethers.Contract(m.contracts.token, ERC20_ABI, p);
        const s = (await token.totalSupply()) as bigint;
        if (!live || !b) return;
        setHead({ number: b.number, timestamp: b.timestamp, txs: b.transactions.length });
        setSupply(s);
        setErr(undefined);
      } catch (e) {
        if (live) setErr((e as Error).message);
      }
    };
    tick();
    const h = setInterval(tick, 5000);
    return () => { live = false; clearInterval(h); };
  }, [m]);

  return (
    <>
      <h1>SecureX devnet</h1>
      <p className="tagline">{BRAND.tagline}</p>
      <p className="sub">
        SecureX — a private settlement chain running one validator, zero gas, and a
        block only when there is something to put in it. SX is its native token;
        SettlX is the application that runs on it.
      </p>

      {err && <p className="err">chain unreachable: {err}</p>}

      <div className="grid g3">
        <div className="card stat">
          <div className="k">Block height</div>
          <div className="v">{head ? head.number.toLocaleString() : "—"}</div>
          <div className="dim" style={{ fontSize: 13 }}>
            {head ? `last block ${ago(head.timestamp)}` : ""}
          </div>
        </div>
        <div className="card stat">
          <div className="k">Chain id</div>
          <div className="v">{m.chainId}</div>
          <div className="dim" style={{ fontSize: 13 }}>{m.network}</div>
        </div>
        <div className="card stat">
          <div className="k">{m.token.symbol} supply</div>
          <div className="v">{supply === undefined ? "—" : fmtUnits(supply, m.token.decimals)}</div>
          <div className="dim" style={{ fontSize: 13 }}>minted through the Issuer only</div>
        </div>
      </div>

      <h2>Why the height barely moves</h2>
      <div className="note">
        This chain produces a block within two seconds of a transaction arriving, and
        otherwise <strong>one block a day</strong>. It was producing 43,200 a day, nearly all
        empty. An idle chain here is a working chain — measured, not assumed: a transaction
        sent 12 and 25 seconds into an idle window was mined in 2 and 1 seconds.
      </div>

      <h2>Deployed contracts</h2>
      <div className="card">
        <table>
          <thead>
            <tr><th>Contract</th><th>Address</th><th>What it does</th></tr>
          </thead>
          <tbody>
            {([
              ["MirrorToken", m.contracts.token, `${m.token.symbol}, the settlement asset. Minted only by the Issuer`],
              ["DealEscrow", m.contracts.escrow, "Holds deal funds; releases on two signatures, never one"],
              ["Issuer", m.contracts.issuer, "The one path supply can be created or retired through"],
              ["AdminRegistry", m.contracts.adminRegistry, "Who holds admin authority, and when they were granted it"],
              ["DevDispenser", m.contracts.dispenser, "Devnet faucet. Funded, not a minter — it cannot inflate supply"],
            ] as const).map(([name, addr, what]) => (
              <tr key={name}>
                <td><strong>{name}</strong></td>
                <td className="mono" title={addr}>{shortHex(addr, 8, 6)}</td>
                <td className="dim">{what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>What this devnet is not</h2>
      <div className="note warn">
        The RPC is reachable from a browser here, which the production design deliberately
        does not allow — its confidentiality rests partly on the node being unreachable.
        A devnet nobody can reach is useless, so this one gives that up. It is a devnet
        property and must not be carried into production.
      </div>

      <p className="dim" style={{ fontSize: 13, marginTop: 18 }}>
        Deployed {new Date(m.deployedAt).toLocaleString()} from {shortHex(m.deployer, 8, 6)}
      </p>
    </>
  );
}
